<template>
  <div class="flex flex-col gap-4 w-full text-slate-700">
    <Filters
      :options="options"
      :showActionButton="true"
      :search="search"
      :resetFilter="resetFilter"
      :resetPagination="resetPagination"
    />

    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center p-16 space-y-3"
    >
      <Loader />
      <span class="text-xs font-semibold text-slate-400 tracking-wide uppercase">
        Loading sessions...
      </span>
    </div>

    <div
      v-else
      class="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden"
    >
      <DataTable
        :value="tableData"
        :lazy="true"
        :paginator="true"
        :rows="selectedLimit"
        :totalRecords="totalElements"
        :first="(currentPage - 1) * selectedLimit"
        dataKey="_id"
        @page="onPage"
        @sort="onSort"
        :sortField="sortField"
        :sortOrder="sortOrder"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rowsPerPageOptions="[15, 25, 50, 100]"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} sessions"
        showCurrentPageReport
        stripedRows
        scrollable
        scrollHeight="flex"
        class="p-datatable-sm text-sm"
      >
        <template #empty>
          <div class="py-12 text-center flex flex-col items-center justify-center gap-2 text-slate-400">
            <i class="fa-solid fa-clock-rotate-left text-3xl text-slate-300"></i>
            <span class="text-sm font-semibold text-slate-600">No session results found</span>
            <span class="text-xs text-slate-400">Try adjusting your date range or filters.</span>
          </div>
        </template>

        <!-- Player ID -->
        <Column field="user_id" header="Player ID" headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="font-bold text-slate-800 text-xs">
              {{ slotProps.data.user_id || player_id }}
            </span>
          </template>
        </Column>

        <!-- Start Time -->
        <Column field="createdAt" header="Start Time" sortable headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs text-slate-700 font-medium">
              {{ slotProps.data.createdAt ? new Date(slotProps.data.createdAt).toLocaleString() : "-" }}
            </span>
          </template>
        </Column>

        <!-- Ping At -->
        <Column field="ping_at" header="Ping At" headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs text-slate-700 font-medium">
              {{ slotProps.data.ping_at ? new Date(slotProps.data.ping_at).toLocaleString() : "-" }}
            </span>
          </template>
        </Column>

        <!-- Disconnected At -->
        <Column field="disconnected_at" header="Disconnected At" sortable headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs text-slate-700 font-medium">
              {{ slotProps.data.disconnected_at ? new Date(slotProps.data.disconnected_at).toLocaleString() : "-" }}
            </span>
          </template>
        </Column>

        <!-- End Time -->
        <Column field="updatedAt" header="End Time" sortable headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs text-slate-700 font-medium">
              {{ slotProps.data.updatedAt ? new Date(slotProps.data.updatedAt).toLocaleString() : "-" }}
            </span>
          </template>
        </Column>

        <!-- IP Address -->
        <Column field="ip" header="IP" headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs text-slate-700 font-mono">
              {{ slotProps.data.ip || "-" }}
            </span>
          </template>
        </Column>

        <!-- Platform -->
        <Column field="platform" header="Platform" headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs text-slate-700">
              {{ slotProps.data.platform || "N.A." }}
            </span>
          </template>
        </Column>

        <!-- Device -->
        <Column field="device" header="Device" headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs text-slate-700">
              {{ slotProps.data.device || "N.A." }}
            </span>
          </template>
        </Column>

        <!-- Duration -->
        <Column field="sessionDurationMs" header="Duration" sortable headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <span class="text-xs font-semibold text-slate-800">
              {{ formatDuration(slotProps.data.sessionDurationMs) }}
            </span>
          </template>
        </Column>

        <!-- Actions -->
        <Column header="Actions" headerClass="text-xs uppercase font-bold text-slate-500 py-3">
          <template #body="slotProps">
            <button
              v-if="slotProps.data.ip"
              @click="openGeolocationInfo(slotProps.data)"
              class="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition-colors cursor-pointer"
              title="View Geolocation Info"
            >
              <i class="fa-solid fa-earth-americas text-xs"></i>
            </button>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>

  <!-- Teleported Geolocation Modal -->
  <Teleport to="body">
    <div class="fixed inset-0 z-[999999] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto" v-if="ipInfo && showDetailModal" @click.self="closeModal">
      <div class="bg-white rounded-3xl shadow-2xl border border-slate-200/80 p-5 md:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col gap-4">
        <div class="flex items-center justify-between pb-3 border-b border-slate-100">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-sm font-bold">
              <i class="fa-solid fa-location-dot"></i>
            </div>
            <h3 class="text-base font-bold text-slate-800 m-0">Geolocation Information</h3>
          </div>
          <button @click="closeModal" class="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors">
            <i class="fa-solid fa-xmark text-sm"></i>
          </button>
        </div>

        <div v-if="!isLoadingIpInfo">
          <IpConnectionInfo :ipInfo="ipInfo" />
        </div>
        <div v-else class="py-12 flex justify-center">
          <Loader />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { getFetch } from "@/api/axiosClient";
import Filters from "../table/Filters.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { formatString } from "@/helpers/date.js";
import { mapGetters } from "vuex";
import IpConnectionInfo from "./IpConnectionInfo.vue";
import Loader from "../Loader.vue";

export default {
  name: "PlayerSessions",
  components: {
    Filters,
    DataTable,
    Column,
    IpConnectionInfo,
    Loader,
  },
  props: {
    player_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      isLoadingIpInfo: false,
      start_date: null,
      end_date: null,
      tableData: [],
      isLoading: false,
      showDetailModal: false,
      ipInfo: null,
      totalElements: 0,
      skinFilt: "",
      selectedLimit: 15,
      options: [
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search for Player ID",
          property: "player_id",
          disabled: true,
        },
        {
          label: "Start Date",
          type: "datetime-local",
          placeholder: "Start Date",
          property: "start_date",
          isDateTime: true,
        },
        {
          label: "End Date",
          type: "datetime-local",
          placeholder: "End Date",
          property: "end_date",
          isDateTime: true,
        },
      ],
      sortField: "createdAt",
      sortOrder: -1,
    };
  },
  computed: {
    ...mapGetters(["allowedSkins", "isAdmin", "isMenuOpen", "chatSkinFilter"]),
  },
  mounted() {
    this.skinFilt = this.player_id.split("_")[0];
    this.start_date = this.getInitialTime("start");
    this.end_date = this.getInitialTime("end");
    this.search();
  },
  methods: {
    getInitialTime(type = "start") {
      const hours = type === "start" ? 0 : 23;
      const minutes = type === "start" ? 0 : 59;
      return formatString(null, { hours, minutes });
    },
    formatDateTimeForAPI(dateTimeString) {
      const utcDateTimeString = dateTimeString + "Z";
      return new Date(utcDateTimeString);
    },
    formatDuration(ms) {
      if (!ms || ms <= 0) return "-";
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${seconds}s`;
    },
    async openGeolocationInfo(row) {
      if (row.ip) {
        await this.getIpInfo(row.ip);
      }
    },
    async getIpInfo(ip) {
      this.isLoadingIpInfo = true;
      this.showDetailModal = true;
      try {
        const res = await fetch(`https://ipwhois.app/json/${ip}`);
        const data = await res.json();
        if (data.success) {
          this.ipInfo = data;
        } else {
          this.ipInfo = null;
        }
      } catch (err) {
        console.error("Error retrieving IP:", err);
        this.ipInfo = null;
      } finally {
        this.isLoadingIpInfo = false;
      }
    },
    closeModal() {
      this.showDetailModal = false;
    },
    async onPage(event) {
      this.selectedLimit = event.rows;
      this.currentPage = event.page + 1;
      await this.search();
    },
    async onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;
      await this.search();
    },
    async search() {
      this.isLoading = true;
      try {
        const params = {
          skin_id: this.skinFilt,
          page: this.currentPage,
          limit: this.selectedLimit,
          sortField: this.sortField,
          sortOrder: this.sortOrder === 1 ? "asc" : "desc",
        };

        if (this.start_date !== null) {
          params.start_at = this.formatDateTimeForAPI(this.start_date);
        }

        if (this.end_date !== null) {
          params.end_at = this.formatDateTimeForAPI(this.end_date);
        }

        params.type = "notlive";

        if (this.player_id) params.player_id = this.player_id.trim();

        const data = await getFetch("dashboard/sessionsForPlayer", params);

        this.tableData = data.data || [];
        this.totalPages = data.totalPages || 1;
        this.totalElements = data.totalItems || 0;
      } catch (error) {
        console.error("Error fetching sessions:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async resetFilter() {
      this.resetPagination();
      this.selectedLimit = 15;
      this.start_date = this.getInitialTime("start");
      this.end_date = this.getInitialTime("end");
      this.sortField = "createdAt";
      this.sortOrder = -1;
      await this.search();
    },
    async resetPagination() {
      this.currentPage = 1;
    },
  },
};
</script>
